import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { useMobileContext } from '../layouts/MobileLayout';
import { API_BASE_URL } from '../lib/utils';
import { generateMIFRef } from '../lib/prfUtils';
import { resolveMdtFormIncident } from '../lib/mdtFormIncident';
import { guardExitForm, hasMinorInjuryFormContent } from '../lib/formGuard';
import MinorInjuryMainScreen from '../components/mobile/MinorInjuryMainScreen';
import ClosingDetailsSection from '../components/prf/sections/ClosingDetailsSection';
import PatientDetailsSection from '../components/prf/sections/PatientDetailsSection';
import NOKDetailsSection from '../components/prf/sections/NOKDetailsSection';
import PresentingComplaintSection from '../components/prf/sections/PresentingComplaintSection';
import AttendingSection from '../components/prf/sections/AttendingSection';
import type {
  Attending,
  ClosingDetails,
  NOKDetails,
  PatientDetails,
  PresentingComplaint,
  PRFSectionStatus,
} from '../types/prf';

type SectionId = 'patientDetails' | 'nokDetails' | 'presentingComplaint' | 'attending' | 'closingDetails';

interface AttendingPerson {
  id: string;
  name: string;
  dealtWithPatient: boolean;
}

const defaultSectionStatus: Record<SectionId, PRFSectionStatus> = {
  patientDetails: 'not_started',
  nokDetails: 'not_started',
  presentingComplaint: 'not_started',
  attending: 'not_started',
  closingDetails: 'not_started',
};

async function resolveQuickActionLocation() {
  let location = { latitude: 0, longitude: 0, address: 'Unknown' };
  if (!navigator.geolocation) return location;
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
    });
    try {
      const geocodeRes = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=18&addressdetails=1`,
        { headers: { 'User-Agent': 'C2-Command-System' } },
      );
      if (geocodeRes.ok) {
        const geocodeData = await geocodeRes.json();
        const addr = geocodeData.address || {};
        const parts = [
          addr.road || addr.street || '',
          addr.suburb || addr.district || '',
          addr.town || addr.city || addr.village || '',
        ].filter(Boolean);
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          address: parts.length > 0
            ? `GPS Location Near: ${parts.join(', ')}`
            : `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
        };
      } else {
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
        };
      }
    } catch {
      location = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
      };
    }
  } catch {
    /* ignore */
  }
  return location;
}

function parseAttendingExtras(attending: Attending) {
  try {
    if (attending.additionalNotes?.trim().startsWith('{')) {
      const parsed = JSON.parse(attending.additionalNotes) as {
        attendingPersons?: AttendingPerson[];
        didDrTreatAssess?: boolean;
        drName?: string;
        drPin?: string;
      };
      return {
        attendingPersons: parsed.attendingPersons?.length
          ? parsed.attendingPersons
          : [{ id: '1', name: attending.primaryClinician || '', dealtWithPatient: false }],
        didDrTreatAssess: parsed.didDrTreatAssess ?? false,
        drName: parsed.drName ?? '',
        drPin: parsed.drPin ?? '',
      };
    }
  } catch {
    /* ignore */
  }
  return {
    attendingPersons: [{ id: '1', name: attending.primaryClinician || '', dealtWithPatient: false }],
    didDrTreatAssess: false,
    drName: '',
    drPin: '',
  };
}

export default function MinorInjuryPage() {
  const navigate = useNavigate();
  const reduxDispatch = useAppDispatch();
  const token = useAppSelector((s) => s.auth.token);
  const { activeDispatch } = useAppSelector((s) => s.dispatch);
  const { mdtResource } = useMobileContext();
  const [currentSection, setCurrentSection] = useState<SectionId | null>(null);
  const [patientDetails, setPatientDetails] = useState<PatientDetails>({});
  const [nokDetails, setNokDetails] = useState<NOKDetails>({});
  const [presentingComplaint, setPresentingComplaint] = useState<PresentingComplaint>({});
  const [attending, setAttending] = useState<Attending>({});
  const [closingDetails, setClosingDetails] = useState<ClosingDetails>({});
  const [sectionStatus, setSectionStatus] = useState(defaultSectionStatus);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => setCurrentSection(null);

  const handleSaveSection = (sectionId: SectionId, data: unknown) => {
    if (sectionId === 'patientDetails') setPatientDetails(data as PatientDetails);
    if (sectionId === 'nokDetails') setNokDetails(data as NOKDetails);
    if (sectionId === 'presentingComplaint') setPresentingComplaint(data as PresentingComplaint);
    if (sectionId === 'attending') setAttending(data as Attending);
    if (sectionId === 'closingDetails') setClosingDetails(data as ClosingDetails);
    setSectionStatus((prev) => ({ ...prev, [sectionId]: 'complete' }));
    setCurrentSection(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!token) return;
    if (!patientDetails.firstName?.trim()) {
      setError('Patient Details: enter first name');
      return;
    }
    if (!patientDetails.lastName?.trim()) {
      setError('Patient Details: enter surname');
      return;
    }
    if (!presentingComplaint.chiefComplaint?.trim()) {
      setError('Presenting Complaint: enter chief complaint');
      return;
    }
    if (!closingDetails.resultOutcome?.trim()) {
      setError('Closing Details: select a result / outcome');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const location = await resolveQuickActionLocation();
      const patientName = `${patientDetails.firstName} ${patientDetails.lastName}`.trim();
      const outcomeLabel = closingDetails.resultOutcomeLabel || closingDetails.resultOutcome;
      const description = [
        `Minor injury — ${patientName}`,
        presentingComplaint.chiefComplaint,
        outcomeLabel,
        closingDetails.actionsTaken,
      ].filter(Boolean).join(' | ');

      const { incidentId, eventId, createdNew } = await resolveMdtFormIncident({
        token,
        mdtResource,
        activeDispatch,
        reduxDispatch,
        createTitle: 'Minor Injury',
        createDescription: `${description} — ${mdtResource?.name || 'MDT unit'} at ${location.address}`,
        createSeverity: 'low',
        createTags: ['minor_injury', 'prf', 'mif'],
        location,
      });

      const mifRef = generateMIFRef(eventId, incidentId);
      const extras = parseAttendingExtras(attending);
      const attendingPayload = {
        primaryClinician: extras.attendingPersons[0]?.name,
        secondaryClinician: extras.attendingPersons[1]?.name,
        additionalNotes: JSON.stringify({
          attendingPersons: extras.attendingPersons,
          didDrTreatAssess: extras.didDrTreatAssess,
          drName: extras.drName,
          drPin: extras.drPin,
        }),
      };

      const prfRes = await fetch(`${API_BASE_URL}/prfs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: mifRef,
          eventId,
          incidentId,
          data: {
            formType: 'minor_injury',
            patientDetails,
            nokDetails,
            presentingComplaint,
            attending: attendingPayload,
            closingDetails,
            sectionStatus: {
              patientDetails: 'complete',
              nokDetails: 'complete',
              presentingComplaint: 'complete',
              pastMedicalHistory: 'not_started',
              allergiesMeds: 'not_started',
              fast: 'not_started',
              observations: 'not_started',
              attending: 'complete',
              closingDetails: 'complete',
            },
          },
        }),
      });
      if (!prfRes.ok) throw new Error('Failed to save minor injury form');

      navigate('/', {
        state: {
          quickActionFeedback: createdNew
            ? `Minor injury saved — ${mifRef} (new incident)`
            : `Minor injury saved — ${mifRef}`,
        },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save minor injury');
    } finally {
      setSubmitting(false);
    }
  };

  if (currentSection === 'patientDetails') {
    return (
      <PatientDetailsSection
        data={patientDetails}
        onSave={(data) => handleSaveSection('patientDetails', data)}
        onBack={handleBack}
      />
    );
  }
  if (currentSection === 'nokDetails') {
    return (
      <NOKDetailsSection
        data={nokDetails}
        onSave={(data) => handleSaveSection('nokDetails', data)}
        onBack={handleBack}
      />
    );
  }
  if (currentSection === 'presentingComplaint') {
    return (
      <PresentingComplaintSection
        data={presentingComplaint}
        onSave={(data) => handleSaveSection('presentingComplaint', data)}
        onBack={handleBack}
      />
    );
  }
  if (currentSection === 'attending') {
    return (
      <AttendingSection
        data={attending}
        onSave={(data) => handleSaveSection('attending', data)}
        onBack={handleBack}
      />
    );
  }
  if (currentSection === 'closingDetails') {
    return (
      <ClosingDetailsSection
        data={closingDetails}
        onSave={(value) => handleSaveSection('closingDetails', value)}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {error && (
        <p className="flex-shrink-0 px-4 py-2 text-xs text-red-400 text-center bg-red-500/10 border-b border-red-500/20">
          {error}
        </p>
      )}
      <MinorInjuryMainScreen
        onSectionSelect={(id) => setCurrentSection(id as SectionId)}
        onBack={() => guardExitForm(
          hasMinorInjuryFormContent({
            patientDetails,
            nokDetails,
            presentingComplaint,
            attending,
            closingDetails,
            sectionStatus,
          }),
          () => navigate(-1),
        )}
        onSubmit={handleSubmit}
        sectionStatuses={sectionStatus}
        submitting={submitting}
      />
    </div>
  );
}
